/**
 * usePolling - 轮询 Hook
 *
 * 🎯 功能：
 * 1. 支持自定义轮询间隔
 * 2. 支持手动启动/停止轮询
 * 3. 支持根据条件自动停止轮询
 * 4. 组件卸载时自动清理
 *
 * 📱 使用场景：
 * - 任务状态轮询
 * - 数据定时刷新
 * - 长时间运行任务的进度查询
 *
 * @example
 * ```tsx
 * const { start, stop, isPolling } = usePolling({
 *   fn: async () => {
 *     const res = await getTaskStatus(taskId);
 *     if (res.status === 'success') {
 *       return { shouldStop: true, data: res };
 *     }
 *     return { shouldStop: false };
 *   },
 *   interval: 2000,
 *   onSuccess: (data) => console.log('任务完成', data),
 *   onError: (error) => console.error('轮询出错', error),
 * });
 *
 * // 开始轮询
 * start();
 *
 * // 停止轮询
 * stop();
 * ```
 */

import { useRef, useCallback, useEffect, useState } from 'react';

export interface PollingResult<T = unknown> {
  /** 是否停止轮询 */
  shouldStop: boolean;
  /** 返回的数据（可选） */
  data?: T;
}

export interface UsePollingOptions<T = unknown> {
  /** 轮询执行的函数，返回 shouldStop 为 true 时停止轮询 */
  fn: () => Promise<PollingResult<T>>;
  /** 轮询间隔时间（毫秒），默认 2000ms */
  interval?: number;
  /** 是否在启动时立即执行一次，默认 true */
  immediate?: boolean;
  /** 轮询成功停止时的回调 */
  onSuccess?: (data?: T) => void;
  /** 轮询出错时的回调 */
  onError?: (error: Error) => void;
  /** 最大轮询次数，超过后自动停止，默认不限制 */
  maxAttempts?: number;
  /** 达到最大轮询次数时的回调 */
  onMaxAttemptsReached?: () => void;
}

export interface UsePollingReturn {
  /** 开始轮询 */
  start: () => void;
  /** 停止轮询 */
  stop: () => void;
  /** 是否正在轮询 */
  isPolling: boolean;
  /** 当前轮询次数 */
  attempts: number;
}

export function usePolling<T = unknown>(options: UsePollingOptions<T>): UsePollingReturn {
  const { fn, interval = 2000, immediate = true, onSuccess, onError, maxAttempts, onMaxAttemptsReached } = options;

  const [isPolling, setIsPolling] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptsRef = useRef(0);
  const isPollingRef = useRef(false);

  // 停止轮询
  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    isPollingRef.current = false;
    setIsPolling(false);
  }, []);

  // 执行轮询函数
  const executePoll = useCallback(async () => {
    // 如果已停止轮询，不再执行
    if (!isPollingRef.current) return;

    attemptsRef.current += 1;
    setAttempts(attemptsRef.current);

    // 检查是否超过最大轮询次数
    if (maxAttempts && attemptsRef.current > maxAttempts) {
      stop();
      onMaxAttemptsReached?.();
      return;
    }

    try {
      const result = await fn();

      if (result.shouldStop) {
        stop();
        onSuccess?.(result.data);
      }
    } catch (error) {
      stop();
      onError?.(error as Error);
    }
  }, [fn, maxAttempts, onError, onMaxAttemptsReached, onSuccess, stop]);

  // 开始轮询
  const start = useCallback(() => {
    // 清除之前的轮询
    stop();

    // 重置计数
    attemptsRef.current = 0;
    setAttempts(0);

    // 设置轮询状态
    isPollingRef.current = true;
    setIsPolling(true);

    // 是否立即执行一次
    if (immediate) {
      executePoll();
    }

    // 设置定时轮询
    intervalRef.current = setInterval(executePoll, interval);
  }, [stop, immediate, executePoll, interval]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return {
    start,
    stop,
    isPolling,
    attempts,
  };
}
